"use client";

import { useState } from "react";
import Image from "next/image";
import { products as catalog } from "@/lib/content/products";

import { isRealPhoto } from "@/lib/brand-mapping";

function realImageForSku(sku: string): string | undefined {
  const p = catalog.find((x) => x.sku === sku);
  return p?.image && isRealPhoto(p.image) ? p.image : undefined;
}

interface SpecialRoutine {
  name: string;
  skinType?: string;
  age?: string;
  steps: Record<string, string | string[] | null>;
}

interface SpecialRoutineCardProps {
  routine: SpecialRoutine;
  dict: Record<string, string>;
}

const STEP_ORDER = ["cleanse", "tone", "treat", "hydrate", "protect"];
const STEP_LABEL: Record<string, string> = {
  cleanse: "Cleanse",
  tone: "Tone",
  treat: "Treat",
  hydrate: "Hydrate",
  protect: "Protect",
  step1: "Step 1",
  step2: "Step 2",
  step3: "Step 3",
};

const NAME_TO_SKU: Record<string, string> = {
  "Bright Glow": "1002",
  "Bright Glow Cleansing Foam": "1002",
  "Bright Glow Cleansing Face Foam": "1002",
  "Clear Deep": "1003",
  "Clear Deep Cleansing Face Foam": "1003",
  "Fresh Calm": "1001",
  "Fresh Calm Cleansing Gel": "1001",
  "80% GFF & Seaweed Tone Essence": "1006",
  "10% GFF & 1% BHA Balance Mist": "1007",
  "10% GFF & BHA Balance Mist": "1007",
  "5x HA & Ceramide Hydra Intensive Serum": "1032",
  "15% Vitamin C + HA Antioxidizing Defense Serum": "1030",
  "15% Vitamin C + HA Collagen Boost Cream": "1031",
  "3% Amino Acids Nourish & Hydrate Cream": "1033",
  "3% Amino Acids Comfort Hydration Gel": "1032",
  "0.1% Retinol Glow Skin Milk": "1020",
  "0.3% Retinol + Bakuchiol Advanced Glow Milk": "1021",
  "3% Peptides + 1% Oligo HA Lifting Serum": "1023",
  "Sun Protection Cream Face SPF 50+": "1082",
  "Matte Shield SPF 50+": "1082",
  "Matte Shield SPF 50+ (AM only)": "1082",
  "5% Vitamin C Blemish Relief Serum": "1015",
  "2% Alpha-Arbutin + 2% Vitamin C Brightening Serum": "1040",
  "1% Alpha-Arbutin Brighten & Protect Cream": "1041",
  "1% Alpha-Arbutin Brighten & Protect Cream SPF30": "1041",
  "5% Vitamin C Blemish Rescue Gel": "1015",
};

function getSkuForProduct(name: string): string | null {
  const cleaned = name.trim();
  if (NAME_TO_SKU[cleaned]) return NAME_TO_SKU[cleaned];
  for (const [key, sku] of Object.entries(NAME_TO_SKU)) {
    if (
      cleaned.toLowerCase().startsWith(key.toLowerCase()) ||
      key.toLowerCase().startsWith(cleaned.toLowerCase())
    ) {
      return sku;
    }
  }
  return null;
}

function ProductRow({ name }: { name: string }) {
  const sku = getSkuForProduct(name);
  const displayName = name.replace(/\s*\(.*?\)\s*/g, "").trim();
  const note = name.match(/\((.*?)\)/)?.[1];
  const src = sku ? realImageForSku(sku) : undefined;

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
      {src ? (
        <div
          style={{
            width: 40,
            height: 40,
            background: "var(--bp-bg-2)",
            border: "1px solid var(--bp-rule)",
            position: "relative",
            overflow: "hidden",
            flexShrink: 0,
          }}
        >
          <Image src={src} alt={displayName} width={40} height={40} style={{ objectFit: "cover", width: "100%", height: "100%" }} />
        </div>
      ) : sku ? (
        <div
          style={{
            width: 40,
            height: 40,
            background: "var(--bp-bg-2)",
            border: "1px solid var(--bp-rule)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontFamily: "var(--bp-mono)",
            fontSize: 9,
            color: "var(--bp-ink-3)",
            letterSpacing: "0.06em",
            flexShrink: 0,
          }}
        >
          {sku}
        </div>
      ) : null}
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 14, color: "var(--bp-ink)", lineHeight: 1.35 }}>
          {displayName}
        </div>
        {note && (
          <div className="bp-mono-sm" style={{ marginTop: 2 }}>
            {note.toUpperCase()}
          </div>
        )}
      </div>
    </div>
  );
}

export default function SpecialRoutineCard({ routine }: SpecialRoutineCardProps) {
  const [expanded, setExpanded] = useState(false);

  const steps = Object.entries(routine.steps).filter(
    ([, val]) => val !== null && val !== undefined
  );

  const sortedSteps = steps.sort(([a], [b]) => {
    const ai = STEP_ORDER.indexOf(a);
    const bi = STEP_ORDER.indexOf(b);
    if (ai === -1 && bi === -1) return 0;
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });

  const productCount = sortedSteps.reduce((acc, [, val]) => {
    if (Array.isArray(val)) return acc + val.length;
    return acc + 1;
  }, 0);

  return (
    <div style={{ borderTop: "1px solid var(--bp-rule)" }}>
      <button
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
        style={{
          width: "100%",
          textAlign: "left",
          padding: "20px 0",
          display: "grid",
          gridTemplateColumns: "1fr auto auto",
          gap: 24,
          alignItems: "center",
          background: "none",
          borderTopWidth: 0,
          borderRightWidth: 0,
          borderLeftWidth: 0,
          borderBottomWidth: 0,
          cursor: "pointer",
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontFamily: "var(--bp-serif)",
              fontSize: 22,
              fontWeight: 300,
              letterSpacing: "-0.01em",
              color: "var(--bp-ink)",
              lineHeight: 1.2,
            }}
          >
            {routine.name}
          </div>
          <div style={{ display: "flex", gap: 12, marginTop: 6, flexWrap: "wrap" }}>
            {routine.skinType && (
              <span className="bp-mono-sm">SKIN · {routine.skinType.toUpperCase()}</span>
            )}
            {routine.age && (
              <span className="bp-mono-sm">AGE · {routine.age.toUpperCase()}</span>
            )}
          </div>
        </div>
        <span className="bp-mono-sm" style={{ whiteSpace: "nowrap" }}>
          {String(steps.length).padStart(2, "0")} STEPS · {String(productCount).padStart(2, "0")} PRODUCTS
        </span>
        <span
          style={{
            fontFamily: "var(--bp-mono)",
            fontSize: 16,
            color: "var(--bp-ink)",
            width: 24,
            textAlign: "center",
          }}
        >
          {expanded ? "−" : "+"}
        </span>
      </button>

      {expanded && (
        <ol
          style={{
            listStyle: "none",
            margin: 0,
            padding: "0 0 20px",
          }}
        >
          {sortedSteps.map(([key, val], idx) => {
            const products = Array.isArray(val) ? (val as string[]) : [val as string];
            const label = STEP_LABEL[key] ?? key;
            return (
              <li
                key={key}
                style={{
                  display: "grid",
                  gridTemplateColumns: "60px 140px 1fr",
                  gap: 20,
                  padding: "16px 0",
                  borderTop: idx === 0 ? "1px solid var(--bp-rule)" : "1px dashed var(--bp-rule-2)",
                  alignItems: "start",
                }}
                className="bp-spec-routine-row"
              >
                <span className="bp-mono" style={{ color: "var(--bp-ink)" }}>
                  {String(idx + 1).padStart(2, "0")}
                </span>
                <span className="bp-mono-sm" style={{ color: "var(--bp-ink)" }}>
                  {label.toUpperCase()}
                </span>
                <div style={{ display: "flex", flexDirection: "column", gap: 12, minWidth: 0 }}>
                  {products.map((p, i) => (
                    <ProductRow key={i} name={p} />
                  ))}
                </div>
              </li>
            );
          })}
        </ol>
      )}

      <style>{`
        @media (max-width: 700px) {
          .bp-spec-routine-row { grid-template-columns: 32px 1fr !important; }
          .bp-spec-routine-row > :nth-child(2) {
            grid-column: 2 / -1;
            margin-top: -12px;
          }
        }
      `}</style>
    </div>
  );
}
